import { useState, useEffect } from 'react';
import {
  useLocation,
  generatePath,
  Outlet,
  useMatch,
  useNavigate,
} from 'react-router-dom';
import { format } from 'date-fns';
import queryString from 'query-string';
import {
  Tabs,
  DateInput,
  DateInputType,
  PickerPosition,
  IconLoader,
  PermissionDenied,
  PermissionEnum,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearRateListState } from './store/slices/list';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const Rates = (): React.JSX.Element => {
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const {
    exchangeDictionaries: { currencies, activePermissions },
  } = useAppSelector((state) => state);

  const { search, pathname } = useLocation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const match = useMatch(ROUTES.RATES);

  useEffect(() => {
    if (
      currencies &&
      currencies.length &&
      match &&
      activePermissions &&
      activePermissions.includes(PermissionEnum.SETTINGS_EXCHANGE_RATES_LIST)
    ) {
      navigate(generatePath(ROUTES.RATES_CURRENCY, { currency: 'all' }));
    }
  }, [currencies, activePermissions, match]);

  useEffect(() => {
    if (
      search &&
      activePermissions &&
      activePermissions.includes(PermissionEnum.SETTINGS_EXCHANGE_RATES_LIST)
    ) {
      const params = queryString.parse(search);

      if (params && params.date) {
        setDate(params.date as string);
      }
    }
  }, [search, activePermissions]);

  const onDateChange = (dateValue: Date) => {
    const dateValueFormatted = format(dateValue, 'yyyy-MM-dd');

    setDate(dateValueFormatted);

    const queryArray = search.split('&');

    const queryFomatted = queryArray
      ?.filter((item) => !item.includes('date'))
      .join('&');

    const query = queryFomatted.length
      ? `${queryFomatted}&date=${dateValueFormatted}`
      : `?date=${dateValueFormatted}`;

    navigate(`${pathname}${query.charAt(0) === '?' ? '' : `?`}${query}`);
  };

  useEffect(
    () => () => {
      dispatch(clearRateListState());
    },
    [],
  );

  useEffect(() => {
    let title = 'Exchange rates';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content = (
    <div className={cn.ratesLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions && currencies && currencies.length) {
    if (
      activePermissions.includes(PermissionEnum.SETTINGS_EXCHANGE_RATES_LIST)
    ) {
      content = (
        <div className={cn.rates}>
          <div className={cn.ratesTopWrapper}>
            <div className={cn.ratesTitle}>Exchange rates</div>
            {currencies && currencies?.length && (
              <div className={cn.ratesControlRow}>
                <Tabs
                  tabs={[
                    {
                      name: 'All',
                      path: `${generatePath(ROUTES.RATES_CURRENCY, {
                        currency: 'all',
                      })}${
                        search
                          ? `?${search
                              .split('&')
                              ?.filter(
                                (item) => !item.includes('currencyLeftId'),
                              )
                              .join('&')}`
                          : ''
                      }`,
                    },
                    ...currencies.slice(0, 7).map((item) => {
                      const url = `${generatePath(ROUTES.RATES_CURRENCY, {
                        currency: item.id,
                      })}${
                        search
                          ? `?${search
                              .split('&')
                              ?.filter(
                                (values) => !values.includes('currencyLeftId'),
                              )
                              .join('&')}`
                          : ''
                      }`;

                      return {
                        name: item.name,
                        path: url,
                      };
                    }),
                  ]}
                  hiddenRoutes={currencies?.slice(7)?.map((item) => {
                    const url = generatePath(ROUTES.RATES_CURRENCY, {
                      currency: item.id,
                    });

                    return {
                      name: item.name,
                      path: url,
                    };
                  })}
                  wrapperClassName={cn.tabsWrapper}
                  itemClassName={cn.tabsItem}
                  enableRouting
                />
                <div className={cn.datePickerWrapper}>
                  <DateInput
                    type={DateInputType.PSEUDO}
                    pickerPisition={PickerPosition.RIGHT}
                    title="Show rates for"
                    placeholder="Select date"
                    value={date}
                    onChange={(dateValue) => onDateChange(dateValue)}
                    onReset={() => onDateChange(new Date())}
                    bold
                  />
                </div>
              </div>
            )}
          </div>
          <Outlet context={{ date, setDate }} />
        </div>
      );
    } else {
      content = <PermissionDenied />;
    }
  }

  return content;
};
export default Rates;
